import type { GalleryFit, GalleryImage, GalleryProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, NumberField, SelectField, TextField } from "./fields.js";
import { MediaField } from "./MediaField.js";
import type { WidgetInspectorProps } from "./registry.js";

const COLS = ["2", "3", "4", "5", "6"] as const;
const FIT = ["cover", "contain"] as const;

function newImageId(): string {
  return `g-${Math.random().toString(36).slice(2, 8)}`;
}

export function GalleryInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as GalleryProps;
  const images = p.images ?? [];

  const setImages = (next: GalleryImage[]) => update(widget.id, { images: next });

  const addImage = () =>
    setImages([
      ...images,
      {
        id: newImageId(),
        src: "https://placehold.co/600x600/e5e7eb/6b7280?text=New",
        alt: "",
      },
    ]);

  const updateImage = (id: string, patch: Partial<GalleryImage>) =>
    setImages(images.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeImage = (id: string) => setImages(images.filter((it) => it.id !== id));

  const moveImage = (id: string, dir: "up" | "down") => {
    const idx = images.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= images.length) return;
    const next = images.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setImages(next);
  };

  return (
    <>
      <SelectField
        label="Columns"
        value={String(p.columns ?? 3) as (typeof COLS)[number]}
        options={COLS}
        onChange={(v) =>
          update(widget.id, {
            columns: Number.parseInt(v, 10) as GalleryProps["columns"],
          })
        }
      />
      <div className="emke-inspector-grid-2" style={{ padding: "0 16px" }}>
        <NumberField
          label="Gap (px)"
          value={p.gap ?? 12}
          min={0}
          max={64}
          onChange={(v) => update(widget.id, { gap: v })}
        />
        <NumberField
          label="Radius (px)"
          value={p.radius ?? 8}
          min={0}
          max={999}
          onChange={(v) => update(widget.id, { radius: v })}
        />
      </div>
      <CheckboxField
        label="Square tiles"
        value={p.square ?? true}
        onChange={(v) => update(widget.id, { square: v })}
      />
      <SelectField
        label="Fit"
        value={p.fit ?? "cover"}
        options={FIT}
        onChange={(v) => update(widget.id, { fit: v as GalleryFit })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Images ({images.length})
      </div>

      {images.map((img, i) => (
        <div
          key={img.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveImage(img.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveImage(img.id, "down")}
              disabled={i === images.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === images.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeImage(img.id)}
              title="Delete image"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <MediaField
            label={`Image ${i + 1} URL`}
            value={img.src}
            onChange={(v) => updateImage(img.id, { src: v })}
          />
          <TextField
            label="Alt text"
            value={img.alt}
            onChange={(v) => updateImage(img.id, { alt: v })}
          />
          <TextField
            label="Caption (optional)"
            value={img.caption ?? ""}
            onChange={(v) => updateImage(img.id, { caption: v || undefined })}
          />
          <TextField
            label="Link URL (optional)"
            value={img.href ?? ""}
            onChange={(v) => updateImage(img.id, { href: v || undefined })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addImage}
          style={{ width: "100%" }}
        >
          + Add image
        </button>
      </div>
    </>
  );
}
